/**
 * useRangeCache.js — the source layer of the gym page: values that outlive the
 * tab that shows them. docs/spec/01f-tab-loading.md, "The source layer".
 *
 * Two hooks share one set of rules — the value survives the component that
 * renders it, it is dropped when the gym changes, and a `status` says whether
 * a consumer may render it:
 *
 *   - `useRangeCache` — a range-keyed one-shot read with a per-source
 *     freshness window (`freshMs`);
 *   - `useKeptListener` — a listener subscribed only while a view holds it
 *     open, whose last value is shown at once on the next open.
 *
 * ## Why the state stays per instance
 *
 * This merges the CODE, not the state. The leaderboard and the admin charts
 * pick independent periods; one shared slot would make each evict the
 * others. Every caller gets its own instance.
 *
 * ## The gym switch
 *
 * `useGymData` does not remount on a gym switch, so a cache that does not
 * reset itself keeps serving the previous gym's rows (it shipped once: gym B's
 * distribution chart showed gym A's climbs for up to a minute). Every hook here
 * resets on a new `gymId`, and a read still in flight for the old gym is
 * discarded when it resolves (the generation counter) instead of landing in
 * the new gym's slot.
 */
import { useState, useRef, useCallback, useEffect } from "react";

const DEFAULT_FRESH_MS = 60 * 1000;

/** True once a source may render: a value, or a definitive failure. */
export function isSettled(status) {
  return status === "ready" || status === "refreshing" || status === "error";
}

// ── Reads in flight, module-wide ────────────────────────────────────────────
// The warm-up waits for the visible view's own reads before it adds traffic of
// its own; counting them here covers every source without each one reporting.
let inFlightReads = 0;
const settleWaiters = new Set();
function readStarted() { inFlightReads += 1; }
function readEnded() {
  inFlightReads = Math.max(0, inFlightReads - 1);
  if (inFlightReads === 0) settleWaiters.forEach((w) => w());
}

/** How many `useRangeCache` reads are in flight right now. */
export function pendingSourceReads() { return inFlightReads; }

/**
 * Resolves once no source read has been in flight for `quietMs` — or after
 * `timeoutMs`, so a hung read never holds the warm-up back for ever.
 */
export function whenSourceReadsSettle({ quietMs = 300, timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    const timers = { quiet: null, cap: null };
    let done = false;
    let arm = null;
    function finish() {
      if (done) return;
      done = true;
      clearTimeout(timers.quiet);
      clearTimeout(timers.cap);
      settleWaiters.delete(arm);
      resolve();
    }
    // Called whenever the counter drops to 0: restart the quiet window.
    arm = () => {
      clearTimeout(timers.quiet);
      timers.quiet = setTimeout(() => { if (inFlightReads === 0) finish(); }, quietMs);
    };
    timers.cap = setTimeout(finish, timeoutMs);
    settleWaiters.add(arm);
    if (inFlightReads === 0) arm();
  });
}

/** Test seam — a clean module counter between suites. */
export function _resetSourceReads() {
  inFlightReads = 0;
  settleWaiters.clear();
}

// A range key and its bounds. `null` on either side means unbounded.
const rangeKey = (from, to) => `${from || ""}|${to || ""}`;
function rangeCovers(held, from, to) {
  if (!held) return false;
  const fromOk = held.from == null || (from != null && from >= held.from);
  const toOk = held.to == null || (to != null && to <= held.to);
  return fromOk && toOk;
}

/**
 * @param {string|null} gymId          resets everything when it changes
 * @param {object}   opts
 * @param {string}   opts.label        used in the error log, e.g. "fetchRankingAscents"
 * @param {Function} opts.load         `(from, to) => Promise<value>` — the actual query
 * @param {*}        [opts.initial]    value before the first load (default `[]`)
 * @param {number}   [opts.freshMs]    freshness window (default 60s) — not a
 *   storage time: an older value stays on screen until its re-read replaces it
 * @param {boolean}  [opts.covers]     a request inside the held range is a hit
 * @param {Function} [opts.overlay]    `(value) => value`, applied to EVERY value a
 *   read stores, at the moment it lands — read through a ref, so it is the latest
 *   one. `useGymData` lays the viewer's own live ascents over the reads with it: a
 *   read that left before the viewer's newest tick answers without it, and a
 *   `patch` cannot help there (on an empty cache it is skipped; on a loaded one the
 *   landing answer replaces the patched value).
 * @returns {[*, Function, object]} `[value, fetch(from, to, force), api]`.
 *   `fetch` resolves to the current value, so a caller that needs the data
 *   (rather than the state) can await it. `api` holds `status`, `isLoaded()`,
 *   `patch(updater)` (how `useGymData` merges the viewer's own live ticks into
 *   a loaded cache) and `invalidate()` (read again next time, keep the value).
 */
export function useRangeCache(gymId, { label, load, initial = [], freshMs = DEFAULT_FRESH_MS, covers = false, overlay = null }) {
  // Value and status are stored WITH the gym they belong to. A render for a new
  // gym derives `initial` / `idle` from the mismatch on its own, so there is no
  // reset effect — one that ran after a child's first `fetch` on the same
  // commit would wipe the status of the read that child had just started.
  const [slot, setSlot] = useState(() => ({ gym: gymId, value: initial, status: "idle" }));

  // Mirrors the value so `fetch` can RETURN it synchronously on the early-out
  // and error paths — callers await the data rather than reading the state,
  // and a stale closure would hand them the previous render's value.
  const valueRef = useRef(initial);
  const heldRef = useRef(null); // { key, from, to } of the value held
  const fetchedAtRef = useRef(0);
  const loadedRef = useRef(false);
  const genRef = useRef(0);
  // The read in flight and its range key. A second call for the SAME range
  // waits for that read instead of answering with the old value at once — a
  // caller that awaits `fetch` to know the data is in would otherwise hear
  // "done" too early.
  const inFlightRef = useRef(null); // { key, promise }
  // A request for ANOTHER range while a read is in flight waits here and runs
  // right after it. Only the latest one is kept: a period stepped twice during
  // one read wants the last period, and every waiter resolves with that read.
  // (Answering it with the current value at once, as before, left that range
  // never read — a chart switched mid-read kept showing the previous window.)
  const queuedRef = useRef(null); // { from, to, force, waiters: Function[] }
  const fetchRef = useRef(null);
  // `load` is read at call time, never as a dependency: it closes over the gym
  // collection helper and would otherwise change `fetch`'s identity every render.
  const loadRef = useRef(load);
  loadRef.current = load;
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;

  // The refs follow the gym during render, before any effect of this commit
  // can call `fetch` for the new gym. Idempotent, so a double render is safe.
  const gymRef = useRef(gymId);
  if (gymRef.current !== gymId) {
    gymRef.current = gymId;
    genRef.current += 1;
    valueRef.current = initial;
    heldRef.current = null;
    fetchedAtRef.current = 0;
    loadedRef.current = false;
    inFlightRef.current = null;
    if (queuedRef.current) { queuedRef.current.waiters.forEach((w) => w(initial)); queuedRef.current = null; }
  }

  const fetch = useCallback(async (from = null, to = null, force = false) => {
    if (!gymId) return valueRef.current;
    const key = rangeKey(from, to);
    const inFlight = inFlightRef.current;
    if (inFlight) {
      if (inFlight.key === key) return inFlight.promise;
      return new Promise((resolve) => {
        const waiters = queuedRef.current ? queuedRef.current.waiters : [];
        waiters.push(resolve);
        queuedRef.current = { from, to, force: force || !!queuedRef.current?.force, waiters };
      });
    }
    const held = heldRef.current;
    const hit = held && (held.key === key || (covers && rangeCovers(held, from, to)));
    const fresh = hit && fetchedAtRef.current && (Date.now() - fetchedAtRef.current) < freshMs;
    if (!force && fresh) return valueRef.current;

    const gen = genRef.current;
    const begun = loadedRef.current ? "refreshing" : "loading";
    setSlot((s) => (s.gym === gymId ? { ...s, status: begun } : { gym: gymId, value: initial, status: begun }));
    readStarted();
    const read = (async () => {
      try {
        const raw = await loadRef.current(from, to);
        if (gen !== genRef.current) return valueRef.current; // another gym by now
        const next = overlayRef.current ? overlayRef.current(raw) : raw;
        valueRef.current = next;
        heldRef.current = { key, from, to };
        fetchedAtRef.current = Date.now();
        loadedRef.current = true;
        setSlot({ gym: gymId, value: next, status: "ready" });
        return next;
      } catch (err) {
        console.error(`[useGymData] ${label} error:`, err);
        if (gen === genRef.current) {
          const after = loadedRef.current ? "ready" : "error";
          setSlot((s) => (s.gym === gymId ? { ...s, status: after } : s));
        }
        return valueRef.current;
      } finally {
        readEnded();
        if (gen === genRef.current) {
          inFlightRef.current = null;
          const queued = queuedRef.current;
          if (queued) {
            queuedRef.current = null;
            Promise.resolve(fetchRef.current(queued.from, queued.to, queued.force))
              .then((v) => queued.waiters.forEach((w) => w(v)));
          }
        }
      }
    })();
    inFlightRef.current = { key, promise: read };
    return read;
    // `initial` is the caller's literal default and stable in practice.
  }, [gymId, label, freshMs, covers]); // eslint-disable-line
  fetchRef.current = fetch;

  /** Has anything been loaded on this gym? The own-ascents merge skips empty caches. */
  const isLoaded = useCallback(() => loadedRef.current, []);

  /** Functional update of the cached value, keeping the ref in step. */
  const patch = useCallback((updater) => {
    setSlot((s) => {
      if (s.gym !== gymRef.current) return s;
      const next = updater(s.value);
      valueRef.current = next;
      return { ...s, value: next };
    });
  }, []);

  /** Forget the age, keep the value: the next `fetch` reads again. */
  const invalidate = useCallback(() => { fetchedAtRef.current = 0; }, []);

  const current = slot.gym === gymId;
  return [
    current ? slot.value : initial,
    fetch,
    { status: current ? slot.status : "idle", isLoaded, patch, invalidate },
  ];
}

/**
 * A listener whose last value outlives its subscription.
 *
 * @param {string|null} gymId
 * @param {object}   opts
 * @param {string}   opts.label         used in the error log
 * @param {string|null} [opts.key]      what the subscription is for; a change
 *   drops the kept value, `null` means "nothing to subscribe" (and clears it)
 * @param {Function} opts.subscribe     `(onNext, onError) => unsubscribe`
 * @param {*}        [opts.initial]     value before the first snapshot (default `null`)
 * @param {boolean}  [opts.clearOnError] go back to `initial` on an error
 * @returns {[*, object]} `[value, { status, open, isLoaded }]`. `open()`
 *   holds the subscription and returns its release — call it in an effect.
 */
export function useKeptListener(gymId, { label, key = "", subscribe, initial = null, clearOnError = false }) {
  // The kept value belongs to one (gym, key) scope. A render for another scope
  // derives `initial` / `idle` from the mismatch, so a previous day's "today"
  // or another gym's numbers never show for a frame.
  const scope = gymId && key != null ? `${gymId}::${key}` : null;
  const [slot, setSlot] = useState(() => ({ scope, value: initial, status: "idle" }));
  const [holders, setHolders] = useState(0);
  const subscribeRef = useRef(subscribe);
  subscribeRef.current = subscribe;
  const current = slot.scope === scope;
  const loaded = current && (slot.status === "ready" || slot.status === "refreshing");
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;

  const active = holders > 0 && !!scope;
  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    setSlot((s) => (s.scope === scope
      ? { ...s, status: s.status === "ready" || s.status === "refreshing" ? "refreshing" : "loading" }
      : { scope, value: initial, status: "loading" }));
    const unsub = subscribeRef.current(
      (next) => { if (live) setSlot({ scope, value: next, status: "ready" }); },
      (err) => {
        if (!live) return;
        console.error(`[source] ${label} listener`, err);
        setSlot((s) => {
          const held = s.scope === scope && (s.status === "ready" || s.status === "refreshing");
          if (clearOnError || !held) return { scope, value: initial, status: "error" };
          return { ...s, status: "ready" };
        });
      },
    );
    return () => {
      live = false;
      if (typeof unsub === "function") unsub();
      // Torn down before its first snapshot (the last holder left, or the gym
      // changed in the same commit): nothing is loading any more.
      setSlot((s) => (s.scope === scope && s.status === "loading" ? { ...s, status: "idle" } : s));
    };
    // `initial`, `label` and `clearOnError` are stable literals at every call site.
  }, [active, scope]); // eslint-disable-line

  // `open()` holds the subscription while its caller is mounted. A source with
  // nothing to subscribe (no gym, `key` null) stays `idle`: a consumer must not
  // gate a card on it.
  const open = useCallback(() => {
    setHolders((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setHolders((n) => Math.max(0, n - 1));
    };
  }, []);

  const isLoaded = useCallback(() => loadedRef.current, []);

  return [current ? slot.value : initial, { status: current ? slot.status : "idle", open, isLoaded }];
}
